import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { fetchPaketDetail } from '../../../services/Master/MasterPaket';
import { tryoutAddResults, tryoutGetRiwayat } from '../../../services/tryoutService';
import moment from 'moment';
import 'moment/locale/id';

moment.locale('id');

const TryOutDetailPageCompact = () => {
  document.title = "Detail Paket Try Out";

  const location = useLocation();
  const navigate = useNavigate();
  const historySectionRef = useRef(null);
  const pageContentRef = useRef(null);

  // --- State Variables ---
  const [tryOutData, setTryOutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStartingTryout, setIsStartingTryout] = useState(false);
  const [fetchedJenisSeleksiId, setFetchedJenisSeleksiId] = useState(null);

  const [tryoutHistory, setTryoutHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [hasNoHistory, setHasNoHistory] = useState(false);

  // --- Modal State for all critical errors ---
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [showSubscriptionButton, setShowSubscriptionButton] = useState(false);
  const params = new URLSearchParams(location.search);

   const idJenisSeleksiFromUrl = params.get('id_jenis_seleksi');
    const idTipeKontenFromUrl = params.get('id_tipe_konten');

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });

  // Function to toggle modal
  const toggleErrorModal = () => setIsErrorModalOpen(!isErrorModalOpen);

  // --- Get URL Query Params with useMemo ---
  const { idPaket, idTipeKonten, idJenisSeleksi } = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      idPaket: queryParams.get('id_paket'),
      idTipeKonten: queryParams.get('id_tipe_konten'),
      idJenisSeleksi: queryParams.get('id_jenis_seleksi'),
    };
  }, [location.search]);

  // --- Scroll to History Section ---
  const scrollToHistorySection = useCallback(() => {
    if (historySectionRef.current) {
      setTimeout(() => {
        historySectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, []);

  // --- Fetch Tryout History Function (memoized with useCallback) ---
  const fetchTryoutHistory = useCallback(async (page = 1, per_page = pagination.per_page) => {
    if (!idPaket) {
      setHistoryError("ID Paket tidak ditemukan untuk mengambil riwayat.");
      setLoadingHistory(false);
      setHasNoHistory(true);
      return;
    }

    setLoadingHistory(true);
    setHistoryError(null);
    setHasNoHistory(false);

    try {
      const response = await tryoutGetRiwayat(idPaket, idTipeKontenFromUrl, idJenisSeleksiFromUrl, page, per_page);

      if (response.success) {
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setTryoutHistory(response.data);
          if (response.meta) {
            setPagination({
              page: response.meta.page || 1,
              per_page: response.meta.per_page || 10,
              total: response.meta.total || 0,
              last_page: response.meta.last_page || Math.ceil((response.meta.total || 0) / (response.meta.per_page || 10))
            });
          }
        } else {
          setTryoutHistory([]);
          setHasNoHistory(true);
          setPagination({ page: 1, per_page: 10, total: 0, last_page: 1 });
        }
      } else {
        setTryoutHistory([]);
        setHasNoHistory(false);
        setHistoryError(response.message || "Gagal mengambil riwayat try out.");
        setPagination({ page: 1, per_page: 10, total: 0, last_page: 1 });
      }
    } catch (err) {
      console.error("Error fetching tryout history:", err);
      setHistoryError("Terjadi kesalahan saat mengambil riwayat try out. Mohon coba lagi.");
      setTryoutHistory([]);
      setHasNoHistory(false);
      setPagination({ page: 1, per_page: 10, total: 0, last_page: 1 });
    } finally {
      setLoadingHistory(false);
    }
  }, [idPaket, pagination.per_page]);

  // --- useEffect hook to fetch try out details & initial history ---
  useEffect(() => {
    const initializeTryoutPage = async () => {
      if (!idPaket || !idTipeKonten || !idJenisSeleksi) {
        setModalErrorMessage("Parameter 'id_paket', 'id_tipe_konten', atau 'id_jenis_seleksi' tidak ditemukan di URL.");
        setShowSubscriptionButton(false);
        setIsErrorModalOpen(true);
        setLoading(false);
        setLoadingHistory(false);
        setHasNoHistory(true);
        return;
      }

      setLoading(true);

      try {
        const detailResponse = await fetchPaketDetail(idPaket, idTipeKonten, idJenisSeleksi);

        if (detailResponse.success && detailResponse.data) {
          const apiData = detailResponse.data;
          let totalQuestionsCalculatedFromDetails = 0;

          const jenisSeleksiId = apiData.id_jenis_seleksi || (apiData.paket_detail && apiData.paket_detail.length > 0 ? apiData.paket_detail[0].id_jenis_seleksi : null);
          setFetchedJenisSeleksiId(jenisSeleksiId);

          const categoriesFormatted = apiData.paket_detail.map(detail => {
            const namaJenis = detail.jenis_soal?.nama_jenis || 'Tidak Diketahui';
            const jumlahSoalKategori = detail.jumlah_soal || 0;
            totalQuestionsCalculatedFromDetails += jumlahSoalKategori;
            return {
              name: `Soal ${namaJenis}`,
              count: jumlahSoalKategori,
              icon: getCategoryIcon(namaJenis),
              cardClass: "bg-light text-dark",
              iconBgClass: getCategoryIconBgClass(namaJenis),
            };
          });

          const formattedData = {
            name: apiData.nama_paket,
            status: apiData.is_premium ? 'PREMIUM' : 'GRATIS',
            totalQuestions: apiData.jumlah_soal || totalQuestionsCalculatedFromDetails,
            duration: apiData.durasi_menit,
            categories: categoriesFormatted,
          };
          setTryOutData(formattedData);

          await fetchTryoutHistory(1, pagination.per_page);

        } else {
          setModalErrorMessage(detailResponse.message || "Data paket try out tidak ditemukan atau ada masalah.");
          setShowSubscriptionButton(false);
          setIsErrorModalOpen(true);
          setTryOutData(null);
          setFetchedJenisSeleksiId(null);
          setLoadingHistory(false);
          setHasNoHistory(true);
        }
      } catch (err) {
        console.error("Error initializing paket data:", err);

        const errorMessage = err.message || "Terjadi kesalahan saat mengambil data paket. Mohon coba lagi nanti.";
        if (errorMessage.includes("anda belum berlangganan")) {
          setModalErrorMessage("Maaf, Anda belum memiliki akses ke paket ini. Silakan berlangganan untuk bisa mengerjakan Try Out.");
          setShowSubscriptionButton(true);
        } else {
          setModalErrorMessage(errorMessage);
          setShowSubscriptionButton(false);
        }
        
        setIsErrorModalOpen(true);
        setTryOutData(null);
        setFetchedJenisSeleksiId(null);
        setLoadingHistory(false);
        setHasNoHistory(true);
      } finally {
        setLoading(false);
      }
    };

    initializeTryoutPage();
  }, [idPaket, idTipeKonten, idJenisSeleksi, fetchTryoutHistory, pagination.per_page]);

  // --- Auto scroll to history when it's loaded ---
  useEffect(() => {
    if (!loadingHistory && tryoutHistory.length > 0) {
      // Optional: Auto scroll to history when data is loaded
      // scrollToHistorySection();
    }
  }, [loadingHistory, tryoutHistory.length, scrollToHistorySection]);

  // --- Helper Functions for Icons and Category Colors ---
  const getCategoryIcon = useCallback((categoryName) => {
    switch (categoryName.toLowerCase()) {
      case 'twk':
        return "mdi-account-tie-outline";
      case 'tiu':
        return "mdi-lightbulb-outline";
      case 'tkp':
        return "mdi-human-greeting";
      case 'manajerial':
        return "mdi-briefcase-outline";
      case 'sosial kultural':
        return "mdi-account-group-outline";
      case 'wawancara':
        return "mdi-comment-text-outline";
      default:
        return "mdi-help-circle-outline";
    }
  }, []);

  const getCategoryIconBgClass = useCallback((categoryName) => {
    switch (categoryName.toLowerCase()) {
      case 'twk':
        return "bg-info-subtle text-info";
      case 'tiu':
        return "bg-primary-subtle text-primary";
      case 'tkp':
        return "bg-success-subtle text-success";
      case 'manajerial':
        return "bg-warning-subtle text-warning";
      case 'sosial kultural':
        return "bg-danger-subtle text-danger";
      case 'wawancara':
        return "bg-dark-subtle text-dark";
      default:
        return "bg-secondary-subtle text-secondary";
    }
  }, []);

  const getStatusBadgeColor = useCallback((tipe) => {
    if (tipe === 'GRATIS') {
      return 'bg-success-subtle text-success';
    } else if (tipe === 'PREMIUM') {
      return 'bg-info-subtle text-info';
    }
    return 'bg-secondary-subtle text-secondary';
  }, []);

  const getHistoryStatusBadgeColor = useCallback((status) => {
    if (status === 'LULUS') {
      return 'bg-success-subtle text-success';
    } else if (status === 'TIDAK LULUS') {
      return 'bg-danger-subtle text-danger';
    }
    return 'bg-secondary-subtle text-secondary';
  }, []);

  const getScoreBadgeColor = useCallback((score) => {
    if (score >= 400) return 'bg-success-subtle text-success';
    if (score >= 300) return 'bg-warning-subtle text-warning';
    return 'bg-danger-subtle text-danger';
  }, []);

  const getCategoryScoreColor = useCallback((score) => {
    if (score >= 100) return 'text-success';
    if (score >= 50) return 'text-warning';
    return 'text-danger';
  }, []);

  // --- Event Handler for "Kerjakan" Button ---
  const handleKerjakan = async () => {
    if (!idPaket || !fetchedJenisSeleksiId || !idTipeKonten) {
      setModalErrorMessage("Parameter tidak lengkap. Tidak dapat memulai pengerjaan.");
      setShowSubscriptionButton(false);
      setIsErrorModalOpen(true);
      return;
    }

    setIsStartingTryout(true);
    try {
      const response = await tryoutAddResults(idPaket, fetchedJenisSeleksiId, idTipeKonten);

      if (response.success && response.data) {
        const resultId = response.data;
        let navigateUrl = `/tryout-exam?id_paket=${idPaket}&id_jenis_seleksi=${fetchedJenisSeleksiId}&id_tipe_konten=${idTipeKonten}&result_id=${resultId}`;
        navigate(navigateUrl);
      } else {
        setModalErrorMessage(response.message || "Gagal membuat entri hasil Try Out: Terjadi kesalahan yang tidak diketahui.");
        setShowSubscriptionButton(false);
        setIsErrorModalOpen(true);
      }
    } catch (err) {
      console.error("Error initiating tryout results:", err);
      setModalErrorMessage("Terjadi kesalahan saat memulai Try Out. Mohon coba lagi.");
      setShowSubscriptionButton(false);
      setIsErrorModalOpen(true);
    } finally {
      setIsStartingTryout(false);
    }
  };

  // --- Event Handler for "Batal" Button ---
  const handleBatal = () => {
    navigate(-1);
  };

  // Function to navigate to detail history.
  const handleViewResult = (resultId) => {
    navigate(`/tryout-result-detail?result_id=${resultId}&id_paket=${idPaket}&id_jenis_seleksi=${fetchedJenisSeleksiId || ''}&id_tipe_konten=${idTipeKonten || ''}`);
  };

  // --- Scroll to Top Function ---
  const scrollToTop = () => {
    if (pageContentRef.current) {
      pageContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // --- Custom Pagination Component ---
  const CustomPagination = useCallback(() => {
    const { page, last_page, total, per_page } = pagination;

    if (total === 0) {
      return null;
    }

    const getPageNumbers = () => {
      const pageNumbers = [];
      const maxPagesToShow = 5;

      let startPage, endPage;
      if (last_page <= maxPagesToShow) {
        startPage = 1;
        endPage = last_page;
      } else {
        const pagesBefore = Math.floor((maxPagesToShow - 1) / 2);
        const pagesAfter = Math.ceil((maxPagesToShow - 1) / 2);

        if (page <= pagesBefore) {
          startPage = 1;
          endPage = maxPagesToShow;
        } else if (page + pagesAfter >= last_page) {
          startPage = last_page - maxPagesToShow + 1;
          endPage = last_page;
        } else {
          startPage = page - pagesBefore;
          endPage = page + pagesAfter;
        }
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push('...');
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < last_page) {
        if (endPage < last_page - 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(last_page);
      }
      return pageNumbers;
    };

    const pageNumbers = getPageNumbers();

    const startEntry = total > 0 ? (page - 1) * per_page + 1 : 0;
    const endEntry = Math.min(page * per_page, total);

    return (
      <Row className="mt-3">
        <Col className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <p className="text-muted mb-0 font-size-13">
              Menampilkan {startEntry} sampai {endEntry} dari total {total} entri
            </p>

            <Pagination className="pagination-rounded mb-0">
              <PaginationItem disabled={page === 1}>
                <PaginationLink previous onClick={() => fetchTryoutHistory(page - 1)} />
              </PaginationItem>
              {pageNumbers.map((pageNumber, index) =>
                pageNumber === '...' ? (
                  <PaginationItem key={index} disabled>
                    <PaginationLink>...</PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={index} active={pageNumber === page}>
                    <PaginationLink onClick={() => fetchTryoutHistory(pageNumber)}>
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem disabled={page === last_page}>
                <PaginationLink next onClick={() => fetchTryoutHistory(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </Col>
      </Row>
    );
  }, [pagination, fetchTryoutHistory]);

  // --- Conditional Rendering for Loading/Error States ---
  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <Spinner color="primary" className="me-2" /> Memuat detail Paket...
      </div>
    );
  }

  if (!tryOutData) {
    return (
      <div className="page-content">
        <Container fluid className="py-3">
          <Breadcrumbs title="Detail Paket" breadcrumbItem="Tidak Ditemukan" />
          <Alert color="info" className="text-center mt-3">
            <i className="mdi mdi-information-outline me-2"></i> Data Paket tidak ditemukan untuk ID ini.
          </Alert>
          <div className="text-center mt-3">
             <Button color="secondary" onClick={() => window.history.back()}>Kembali</Button>
          </div>
        </Container>
      </div>
    );
  }

  // --- Main Component View When Data is Successfully Loaded ---
  return (
    <React.Fragment>
      {/* Floating Scroll to Top Button */}
      <Button 
        color="primary" 
        className="rounded-circle position-fixed"
        style={{ 
          bottom: '20px', 
          right: '20px', 
          width: '50px', 
          height: '50px', 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={scrollToTop}
      >
        <i className="mdi mdi-arrow-up"></i>
      </Button>

      <div 
        className="page-content" 
        ref={pageContentRef}
        style={{ 
          minHeight: '100vh', 
          overflowY: 'auto',
          position: 'relative'
        }}
      >
        <Container fluid style={{ paddingBottom: '50px' }}>
          <Breadcrumbs title="Detail Paket" breadcrumbItem={tryOutData.name} />

          <Row className="mb-3">
            <Col lg={12}>
              <Alert color="warning" className="d-flex align-items-start border-0 rounded p-3 custom-alert-warning-bg">
                <div className="flex-shrink-0 me-3 mt-1">
                  <i className="mdi mdi-alert-outline font-size-20 text-warning"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="alert-heading text-dark mb-2">Perhatian Penting Sebelum Mengerjakan Try Out:</h6>
                  <ul className="list-unstyled mb-0 font-size-13 text-dark">
                    <li className="mb-1">1. Kami menyarankan agar kamu mengerjakan soal menggunakan PC/Laptop untuk pengalaman pengguna lebih baik.</li>
                    <li className="mb-1">2. Pastikan koneksi internet kamu stabil.</li>
                    <li className="mb-1">3. Gunakan browser versi terbaru agar website dapat diakses dengan lancar tanpa masalah.</li>
                    <li>4. Pastikan tidak ada aktivitas login ke akun kamu (pada perangkat lain) saat sedang mengerjakan soal.</li>
                  </ul>
                </div>
              </Alert>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0 rounded">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
                    <h5 className="card-title mb-0 flex-grow-1 text-dark">{tryOutData.name}</h5>
                    {tryOutData.status && (
                      <span className={`badge font-size-12 px-2 py-1 rounded-pill ${getStatusBadgeColor(tryOutData.status)}`}>
                        {tryOutData.status}
                      </span>
                    )}
                  </div>

                  <Row className="text-center mb-3 align-items-center justify-content-center g-2">
                    <Col xs={6} md={4}>
                      <Card className="shadow-sm border-0 h-100 transition-300ms hover-zoom-soft">
                        <CardBody className="p-2">
                          <div className="avatar-sm mx-auto mb-2 bg-info-subtle text-info rounded-circle d-flex align-items-center justify-content-center">
                            <i className="mdi mdi-file-document-multiple-outline font-size-18"></i>
                          </div>
                          <p className="font-size-13 text-muted mb-1">Jumlah Soal</p>
                          <h6 className="fw-bold text-dark">{tryOutData.totalQuestions} <small className="text-muted">Soal</small></h6>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col xs={6} md={4}>
                      <Card className="shadow-sm border-0 h-100 transition-300ms hover-zoom-soft">
                        <CardBody className="p-2">
                          <div className="avatar-sm mx-auto mb-2 bg-warning-subtle text-warning rounded-circle d-flex align-items-center justify-content-center">
                            <i className="mdi mdi-timer-outline font-size-18"></i>
                          </div>
                          <p className="font-size-13 text-muted mb-1">Waktu Pengerjaan</p>
                          <h6 className="fw-bold text-dark">{tryOutData.duration} <small className="text-muted">Menit</small></h6>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <h6 className="card-title mb-2 text-center text-muted">Detail Kategori Soal</h6>
                  <Row className="justify-content-center g-2 mb-3">
                    {tryOutData.categories.map((category, index) => (
                      <Col key={index} xs={6} md={4} lg={3} className="d-flex">
                        <Card className={`shadow-sm flex-fill border-0 transition-300ms hover-zoom-soft ${category.cardClass}`}>
                          <CardBody className="py-2 px-2 text-center">
                            <div className="avatar-xs mx-auto mb-1 rounded-circle d-flex align-items-center justify-content-center bg-secondary-subtle text-secondary">
                               <i className={`mdi ${category.icon} font-size-16`}></i>
                            </div>
                            <p className="mb-0 font-size-12 text-dark">{category.name}</p>
                            <h6 className="fw-semibold mb-0 text-dark">{category.count} <small className="text-muted font-size-11">Soal</small></h6>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <Button
                      color="primary"
                      className="btn-sm px-3 py-1 rounded shadow-sm"
                      onClick={handleKerjakan}
                      disabled={isStartingTryout || !idPaket || !fetchedJenisSeleksiId || !idTipeKonten}
                    >
                      {isStartingTryout ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Memulai...
                        </>
                      ) : (
                        <>
                          Kerjakan <i className="mdi mdi-arrow-right ms-1 font-size-14"></i>
                        </>
                      )}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      className="btn-sm px-3 py-1 rounded shadow-sm"
                      onClick={handleBatal}
                      disabled={false}
                    >
                      Batal
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Riwayat Pengerjaan Try Out Section */}
          <Row className="mt-4" ref={historySectionRef}>
            <Col lg={12}>
              <Card className="shadow-sm border-0">
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="card-title mb-0 text-dark">Riwayat Pengerjaan Try Out</h6>
                  </div>
                  
                  {loadingHistory ? (
                    <div className="text-center py-3">
                      <Spinner color="secondary" size="sm" className="me-2" /> Memuat riwayat...
                    </div>
                  ) : historyError ? (
                    <Alert color="danger" className="text-center font-size-14 py-2 rounded" role="alert">
                      <i className="mdi mdi-alert-circle-outline me-1"></i> {historyError}
                      <div className="mt-2">
                        <Button color="danger" outline size="sm" onClick={() => fetchTryoutHistory(pagination.page, pagination.per_page)}>
                          Coba Lagi
                        </Button>
                      </div>
                    </Alert>
                  ) : hasNoHistory ? (
                    <div className="alert alert-info border-0 text-center font-size-14 py-2 rounded" role="alert">
                      <i className="mdi mdi-information-outline me-1"></i> Belum ada riwayat pengerjaan untuk Try Out ini.
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <Table className="table mb-0 table-hover table-striped">
                          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th className="text-center">No</th>
                              <th>Tanggal Pengerjaan</th>
                              <th className="text-center">TWK</th>
                              <th className="text-center">TIU</th>
                              <th className="text-center">TKP</th>
                              <th className="text-center">Total Skor</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tryoutHistory.map((historyItem, index) => (
                              <tr key={historyItem.id_tryout_answer}>
                                <td className="text-center">{(pagination.page - 1) * pagination.per_page + index + 1}</td>
                                <td>
                                  {historyItem.created_at
                                    ? moment.utc(historyItem.created_at).format('DD MMMM YYYY')
                                    : 'N/A'}
                                </td>
                                <td className="text-center">
                                  <Badge color="" className={`font-size-14 fw-bold px-2 py-1 ${getCategoryScoreColor(historyItem.twk)}`}>
                                    {historyItem.twk || '-'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Badge color="" className={`font-size-14 fw-bold px-2 py-1 ${getCategoryScoreColor(historyItem.tiu)}`}>
                                    {historyItem.tiu || '-'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Badge color="" className={`font-size-14 fw-bold px-2 py-1 ${getCategoryScoreColor(historyItem.tkp)}`}>
                                    {historyItem.tkp || '-'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Badge color="" className={`font-size-14 fw-bold px-2 py-1 ${getScoreBadgeColor(historyItem.total)}`}>
                                    {historyItem.total || '-'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Badge color="" className={`rounded-pill font-size-12 px-2 py-1 ${getHistoryStatusBadgeColor(historyItem.status)}`}>
                                    {historyItem.status || 'Belum Selesai'}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Button
                                    color="info"
                                    size="sm"
                                    className="rounded-pill px-3"
                                    onClick={() => handleViewResult(historyItem.id_tryout_answer)}
                                  >
                                    Lihat <i className="mdi mdi-chart-line ms-1"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <CustomPagination />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* NEW: Error Modal Component */}
      <Modal isOpen={isErrorModalOpen} toggle={toggleErrorModal} centered>
        <ModalHeader toggle={toggleErrorModal}>
          <i className="mdi mdi-alert-circle-outline font-size-18 text-danger me-2"></i> Terjadi Kesalahan
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-danger font-size-14 mb-0 text-center">
            {modalErrorMessage}
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          {showSubscriptionButton && (
            <Button color="primary" onClick={() => navigate('/pricing')}>
              Berlangganan
            </Button>
          )}
          <Button color="secondary" onClick={toggleErrorModal}>
            Tutup
          </Button>
        </ModalFooter>
      </Modal>

      {/* Custom Styles */}
      <style>
        {`
        /* --- General Utilities --- */
        .transition-300ms {
          transition: all 0.3s ease-in-out;
        }
        .hover-zoom-soft:hover {
          transform: translateY(-2px);
        }
        .hover-shadow:hover {
          box-shadow: 0 .15rem .3rem rgba(0,0,0,.05)!important;
        }

        /* --- Avatar Sizes --- */
        .avatar-xs {
            width: 30px;
            height: 30px;
            font-size: 14px;
        }
        .avatar-sm {
            width: 50px;
            height: 50px;
            font-size: 18px;
        }

        /* --- Font Sizes --- */
        .font-size-11 { font-size: 0.6875rem !important; }
        .font-size-12 { font-size: 0.75rem !important; }
        .font-size-13 { font-size: 0.8125rem !important; }
        .font-size-14 { font-size: 0.875rem !important; }
        .font-size-15 { font-size: 0.9375rem !important; }
        .font-size-16 { font-size: 1rem !important; }
        .font-size-18 { font-size: 1.125rem !important; }
        .font-size-20 { font-size: 1.25rem !important; }
        .font-size-22 { font-size: 1.375rem !important; }

        /* --- Custom Alert Styles --- */
        .custom-alert-warning-bg {
            background-color: #ffe6cc; /* Light orange background */
            color: #663300; /* Darker orange text */
        }
        .custom-alert-warning-bg .alert-heading,
        .custom-alert-warning-bg .list-unstyled li {
            color: #663300;
        }
        .custom-alert-warning-bg .mdi-alert-outline {
            color: #cc6600; /* Orange icon */
        }

        /* --- Subtle Background Colors & Text Colors (Bootstrap-like) --- */
        .bg-success-subtle { background-color: #d4edda !important; } /* Light Green */
        .text-success { color: #155724 !important; } /* Dark Green */
        .bg-info-subtle { background-color: #d1ecf1 !important; } /* Light Blue */
        .text-info { color: #0c5460 !important; } /* Dark Blue */
        .bg-warning-subtle { background-color: #fff3cd !important; } /* Light Yellow */
        .text-warning { color: #856404 !important; } /* Dark Yellow */
        .bg-primary-subtle { background-color: #cce5ff !important; } /* Lighter Blue */
        .text-primary { color: #004085 !important; } /* Darker Blue */
        .bg-secondary-subtle { background-color: #e2e3e5 !important; } /* Light Gray */
        .text-secondary { color: #383d41 !important; } /* Dark Gray */
        .bg-danger-subtle { background-color: #f8d7da !important; } /* Light Red */
        .text-danger { color: #721c24 !important; } /* Dark Red */
        .bg-dark-subtle { background-color: #d6d8d9 !important; } /* Slightly darker light gray */
        .text-dark { color: #1b1e21 !important; } /* Very Dark Gray/Near Black */

        /* --- Table Styling --- */
        .table-hover tbody tr:hover {
            background-color: rgba(0, 0, 0, 0.03); /* Lighter hover effect */
        }
        .table thead th {
            font-weight: 600; /* Make header text bolder */
            vertical-align: middle; /* Center text vertically */
        }
        .table tbody td {
            vertical-align: middle; /* Center text vertically in cells */
        }

        /* --- Scrollbar Styling --- */
        .table-responsive::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .table-responsive::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .table-responsive::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }
        .table-responsive::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* --- Ensure proper scrolling --- */
        html, body, #root {
          height: 100%;
          overflow-y: auto;
        }
        `}
      </style>
    </React.Fragment>
  );
};

export default TryOutDetailPageCompact;